// Standard page sizes in inches
export const PAGE_SIZES = {
  'us-letter': {
    width: 8.5,
    height: 11,
    label: 'US Letter (8.5 x 11 in)',
    orientation: 'portrait'
  },
  'us-trade': {
    width: 6,
    height: 9,
    label: 'US Trade (6 x 9 in)',
    orientation: 'portrait'
  },
  'square': {
    width: 8.5,
    height: 8.5,
    label: 'Square (8.5 x 8.5 in)',
    orientation: 'square'
  },
  'comic': {
    width: 6.625,
    height: 10.25,
    label: 'Comic Book (6.625 x 10.25 in)',
    orientation: 'portrait'
  },
  'landscape-custom': {
    width: 12.382,
    height: 9.25,
    label: 'Landscape Custom (12.382 x 9.25 in)',
    orientation: 'landscape'
  }
};

// Base dimension for scaling calculations (px)
// For portrait, we use BASE_WIDTH; for landscape, we use BASE_HEIGHT
export const BASE_WIDTH = 600;
export const BASE_HEIGHT = 600;

/**
 * Calculate dimensions maintaining aspect ratio
 * @param {string} pageSize - Key of the page size or custom dimensions
 * @param {Object} customDimensions - Optional custom dimensions for non-standard sizes
 * @returns {Object} Object with width and height in pixels
 */
export function calculateDimensions(pageSize, customDimensions = null) {
  // Handle custom dimensions if provided
  if (customDimensions) {
    const { width, height, orientation = 'portrait' } = customDimensions;
    return calculateCustomDimensions(width, height, orientation);
  }
  
  // Handle predefined sizes
  const size = PAGE_SIZES[pageSize];
  if (!size) return { width: BASE_WIDTH, height: 800 }; // Default fallback
  
  return calculateCustomDimensions(size.width, size.height, size.orientation);
}

/**
 * Calculate dimensions for custom sizes
 * @param {number} width - Width in inches
 * @param {number} height - Height in inches
 * @param {string} orientation - 'portrait', 'landscape', or 'square'
 * @returns {Object} Object with width and height in pixels
 */
function calculateCustomDimensions(width, height, orientation = 'portrait') {
  // Special case for landscape-custom Lulu cover size
  if (width === 12.382 && height === 9.25 && orientation === 'landscape') {
    // Use exact pixel dimensions to match the Lulu cover PDF output
    // This ensures what you see in design is exactly what you get in PDF
    const pixelWidth = 891.5; // 12.382 inches * 72 points per inch
    const pixelHeight = 666.0; // 9.25 inches * 72 points per inch
    
    return { 
      width: pixelWidth, 
      height: pixelHeight, 
      orientation: 'landscape',
      isExactSize: true  // Flag to indicate this is using exact PDF dimensions
    };
  }
  
  // Standard calculation for other sizes
  const aspectRatio = height / width;
  
  if (orientation === 'landscape' || (width > height && orientation !== 'portrait')) {
    // For landscape, base on height
    const pixelHeight = BASE_HEIGHT;
    const pixelWidth = pixelHeight / aspectRatio;
    return { width: pixelWidth, height: pixelHeight, orientation: 'landscape' };
  } else {
    // For portrait or square, base on width
    const pixelWidth = BASE_WIDTH;
    const pixelHeight = pixelWidth * aspectRatio;
    return { width: pixelWidth, height: pixelHeight, orientation: orientation };
  }
}